import React, { FC, useEffect, useRef, useState } from 'react'
import './collapsible-element.css'

// UTILITIES
import { flatten } from '@utils/string-helper'


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    disabled?: boolean
    initState?: 'open' | 'closed'
    state?: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
    onStateChange?: (isOpen: boolean) => void
    children: [React.ReactNode, React.ReactNode]
}

const CollapsibleElement: FC<Props> = (props) => {
    const { className: classProps, disabled, initState = 'open', state = useState(initState === 'open'), onStateChange, children, ...divProps } = props
    const [height, setHeight] = useState(initState === 'open' ? undefined : 0)
    const [isOpen, setIsOpen] = state
    
    const contentRef = useRef<HTMLDivElement>(null)
    const className = flatten('collapsible-element', classProps, disabled ? 'disabled' : undefined)

    useEffect(() => {
        setHeight(isOpen ? contentRef.current?.scrollHeight : 0)
    }, [isOpen])

    useEffect(() => {
        const el = contentRef.current
        if (!el) return

        setHeight(isOpen ? el.scrollHeight : 0)

        const ro = new ResizeObserver(() => {
            if (!isOpen) return
            else setHeight(el.scrollHeight)
        })

        ro.observe(el)

        return () => ro.disconnect()
    }, [isOpen])

    function toggleIsOpen(state = !isOpen) {
        if (onStateChange) onStateChange(state)
        else setIsOpen(state)
    }

    return (
        <div className={className} {...divProps} data-state={isOpen ? 'open' : 'closed'}>
            <div className="collapsable-trigger" onClick={() => { toggleIsOpen() }}>
                {children[0]}
            </div>
            <div className="collapsable-content" style={{ height: isOpen ? height : 0 }}>
                <div ref={contentRef}>
                    {children[1]}
                </div>
            </div>
        </div>
    )
}

export default CollapsibleElement